import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { SurveyService } from '../../../service/survey.service';
import { MessageService } from 'src/app/service/message.service';
import { SurveyNewSavingsRequest } from 'src/app/model/survey-request';
import { decodeFlag, encodeFlag } from 'src/app/utils/common.functions';
import { NdgDataEmit, SurveyNeedsEmit } from 'src/app/model/survey-emits';
import { familyIncomeSourceIncongruence, incomeSavingsIncongruence } from 'src/app/utils/incongruence';
import { SurveySavingsResponse } from 'src/app/model/survey-response';

@Component({
  selector: 'savings',
  templateUrl: './savings.component.html'
})
export class SavingsComponent implements OnChanges {
  @Input() surveyId!: string;
  @Input() surveyNeeds!: SurveyNeedsEmit;
  @Input() ndgData!: NdgDataEmit;
  @Input() savings?: SurveySavingsResponse;
  @Output() complete = new EventEmitter<void>();

  formGroup!: FormGroup;
  loading: boolean = false;
  completed: boolean = false;

  constructor(
    private surveyService: SurveyService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the surveyId (new survey) or savings (show survey) inputs have changed
    if((changes['surveyId'] && changes['surveyId'].firstChange) || changes['savings']) {
      this.init();
    }
  }

  init(): void {
    this.formGroup = new FormGroup({
      minimumInvestmentYear: new FormControl(this.savings?.minimumInvestmentYear || 0, [Validators.required, Validators.min(1)]),
      minimumInvestmentMonth: new FormControl(this.savings?.minimumInvestmentMonth || 0, [Validators.required, Validators.min(1)]),
      foresight: new FormControl(this.savings?.foresight, Validators.required),
      objectives: new FormControl(this.savings?.objectives, Validators.required),
      knowledge1: new FormControl(this.savings?.knowledge1 || false),
      possession1: new FormControl(this.savings?.possession1 || false),
      knowledge2: new FormControl(this.savings?.knowledge2 || false),
      possession2: new FormControl(this.savings?.possession2 || false),
      knowledge3: new FormControl(this.savings?.knowledge3 || false),
      possession3: new FormControl(this.savings?.possession3 || false),
      knowledge4: new FormControl(this.savings?.knowledge4 || false),
      possession4: new FormControl(this.savings?.possession4 || false),
      knowledge5: new FormControl(this.savings?.knowledge5 || false),
      operation: new FormControl(this.savings?.operation, Validators.required),
      investment: new FormControl(this.savings?.investment, Validators.required),
      investmentFinancial: new FormControl(this.savings?.investmentFinancial, Validators.required),
      familyIncome: new FormControl(this.savings?.familyIncome, [Validators.required]),
      familyIncomeSource: new FormControl(this.savings?.familyIncomeSource, Validators.required),
      incomeSavings: new FormControl(this.savings?.incomeSavings || 0, [Validators.required, Validators.min(1), Validators.max(100)]),
      riskPropensity: new FormControl(this.savings?.riskPropensity, Validators.required),
      riskTolerance: new FormControl(this.savings?.riskTolerance, Validators.required),
      riskChoice: new FormControl(this.savings?.riskChoice, Validators.required),
      decorrelation: new FormControl(this.savings?.decorrelation, Validators.required),
      productLifeCycle: new FormControl(encodeFlag(this.savings?.productLifeCycle), Validators.required),
      productProgrammed: new FormControl(encodeFlag(this.savings?.productProgrammed), Validators.required),
      productBeneficiary: new FormControl(encodeFlag(this.savings?.productBeneficiary), Validators.required),
      productImmediateIncome: new FormControl(encodeFlag(this.savings?.productImmediateIncome), Validators.required),
      monthlyPayment: new FormControl(encodeFlag(this.savings?.monthlyPayment), Validators.required)
    });

    if(!this.surveyNeeds.savingsInvestment) {
      this.minimumInvestmentYear?.disable();
    }

    if(!this.surveyNeeds.savingsPlan) {
      this.minimumInvestmentMonth?.disable();
    }

    // Set default values for investmentFinancial based on financialHeritage
    const financialHeritage = this.ndgData?.financialHeritage;
    if(financialHeritage < 25000) {
      this.investmentFinancial?.setValue('A');
    } else if(financialHeritage <= 100000) {
      this.investmentFinancial?.setValue('B');
    } else if(financialHeritage <= 500000) {
      this.investmentFinancial?.setValue('C');
    } else {
      this.investmentFinancial?.setValue('D');
    }

    this.investmentFinancial?.disable();

    // Set default values for familyIncome based on averageIncome
    const averageIncome = this.ndgData?.averageIncome;
    if(averageIncome < 15000) {
      this.familyIncome?.setValue('A');
    } else if(averageIncome <= 30000) {
      this.familyIncome?.setValue('B');
    } else if(averageIncome <= 45000) {
      this.familyIncome?.setValue('C');
    } else if(averageIncome <= 70000) {
      this.familyIncome?.setValue('D');
    } else {
      this.familyIncome?.setValue('E');
    }

    this.familyIncome?.disable();

    if(!this.isInsert()) {
      this.completed = true;
      this.formGroup.disable();
    }
  }

  isInsert(): boolean {
    return this.savings === undefined;
  }

  get minimumInvestmentYear() { return this.formGroup.get('minimumInvestmentYear') }
  get minimumInvestmentMonth() { return this.formGroup.get('minimumInvestmentMonth') }
  get foresight() { return this.formGroup.get('foresight') }
  get objectives() { return this.formGroup.get('objectives') }
  get knowledge1() { return this.formGroup.get('knowledge1') }
  get possession1() { return this.formGroup.get('possession1') }
  get knowledge2() { return this.formGroup.get('knowledge2') }
  get possession2() { return this.formGroup.get('possession2') }
  get knowledge3() { return this.formGroup.get('knowledge3') }
  get possession3() { return this.formGroup.get('possession3') }
  get knowledge4() { return this.formGroup.get('knowledge4') }
  get possession4() { return this.formGroup.get('possession4') }
  get knowledge5() { return this.formGroup.get('knowledge5') }
  get operation() { return this.formGroup.get('operation') }
  get investment() { return this.formGroup.get('investment') }
  get investmentFinancial() { return this.formGroup.get('investmentFinancial') }
  get familyIncome() { return this.formGroup.get('familyIncome') }
  get familyIncomeSource() { return this.formGroup.get('familyIncomeSource') }
  get incomeSavings() { return this.formGroup.get('incomeSavings') }
  get riskPropensity() { return this.formGroup.get('riskPropensity') }
  get riskTolerance() { return this.formGroup.get('riskTolerance') }
  get riskChoice() { return this.formGroup.get('riskChoice') }
  get decorrelation() { return this.formGroup.get('decorrelation') }
  get productLifeCycle() { return this.formGroup.get('productLifeCycle') }
  get productProgrammed() { return this.formGroup.get('productProgrammed') }
  get productBeneficiary() { return this.formGroup.get('productBeneficiary') }
  get productImmediateIncome() { return this.formGroup.get('productImmediateIncome') }
  get monthlyPayment() { return this.formGroup.get('monthlyPayment') }

  hasError(field: AbstractControl | null, errorType: string | undefined = undefined) {
    let hasError = !!field && field.touched && !field.disabled && !field.valid;

    if(hasError && !!errorType) {
      return field?.getError(errorType);
    }

    return hasError;
  }

  hasFamilyIncomeSourceIncongruence(incongruenceType: string): boolean {
    return familyIncomeSourceIncongruence(this.ndgData?.occupation)(this.familyIncomeSource?.value) === incongruenceType;
  }

  hasIncomeSavingsCoherence(incongruenceType: string): boolean {
    return incomeSavingsIncongruence(this.familyIncome?.value, this.minimumInvestmentMonth?.value)(this.incomeSavings?.value) === incongruenceType;
  }

  onSubmit() {
    if(!this.formGroup.valid || this.completed) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.formGroup.disable();
    this.completed = true;
    this.loading = true;

    const payload: SurveyNewSavingsRequest = {
      minimumInvestmentYear: this.minimumInvestmentYear?.value,
      minimumInvestmentMonth: this.minimumInvestmentMonth?.value,
      foresight: this.foresight?.value,
      objectives: this.objectives?.value,
      knowledge1: this.knowledge1?.value,
      possession1: this.possession1?.value,
      knowledge2: this.knowledge2?.value,
      possession2: this.possession2?.value,
      knowledge3: this.knowledge3?.value,
      possession3: this.possession3?.value,
      knowledge4: this.knowledge4?.value,
      possession4: this.possession4?.value,
      knowledge5: this.knowledge5?.value,
      operation: this.operation?.value,
      investment: this.investment?.value,
      investmentFinancial: this.investmentFinancial?.value,
      familyIncome: this.familyIncome?.value,
      familyIncomeSource: this.familyIncomeSource?.value,
      incomeSavings: this.incomeSavings?.value,
      riskPropensity: this.riskPropensity?.value,
      riskTolerance: this.riskTolerance?.value,
      riskChoice: this.riskChoice?.value,
      decorrelation: this.decorrelation?.value,
      productLifeCycle: decodeFlag(this.productLifeCycle?.value),
      productProgrammed: decodeFlag(this.productProgrammed?.value),
      productBeneficiary: decodeFlag(this.productBeneficiary?.value),
      productImmediateIncome: decodeFlag(this.productImmediateIncome?.value),
      monthlyPayment: decodeFlag(this.monthlyPayment?.value)
    }

    this.surveyService.insertSavingsData(this.surveyId, payload).subscribe({
      next: () => {
        this.loading = false;
        this.complete.emit();
      },
      error: () => {
        this.messageService.error('survey.new.message.unknown-error');

        this.loading = false;
        this.completed = false;
        this.formGroup.enable();
      }
    });
  }
}
